import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import RedeemModal from "./RedeemModal";

const API_BASE = "https://api-2026.fikacreatives.co";

async function readJsonSafely(r) {
  const ct = r.headers.get("content-type") || "";
  if (ct.includes("application/json")) return await r.json();
  const text = await r.text();
  return {
    ok: false,
    error: `Expected JSON but got ${ct || "unknown"}: ${text.slice(0, 120)}`,
  };
}

export default function RedeemPage() {
  const { cafeUuid } = useParams();

  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");

  const cafeLocation = useMemo(() => ctx?.cafe?.location || "", [ctx]);
  const cafeLogo = useMemo(() => ctx?.cafe?.logo_url || "", [ctx]);
  const cafeSpace = useMemo(() => ctx?.cafe?.cafe_space || "", [ctx]);
  const cafeName = useMemo(() => ctx?.cafe?.name || "Cafe", [ctx]);
  const cafeCity = useMemo(() => ctx?.cafe?.city || "", [ctx]);
  const offerTitle = useMemo(() => ctx?.offer?.title || "Offer", [ctx]);

  const offerActive = useMemo(() => {
    if (!ctx?.offer) return false;
    return !ctx.offer.redeemed_at;
  }, [ctx]);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const r = await fetch(
          `${API_BASE}/api/redeem/context/${encodeURIComponent(cafeUuid)}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
          }
        );

        const data = await readJsonSafely(r);

        if (!alive) return;

        if (!r.ok || !data?.ok) {
          setError(data?.error || `Failed to load (${r.status})`);
          setCtx(null);
          return;
        }

        setCtx(data);
      } catch (e) {
        if (!alive) return;
        console.error("redeem context fetch error:", e);
        setError(e?.message || "Failed to load redemption context.");
        setCtx(null);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    if (cafeUuid) load();

    return () => {
      alive = false;
    };
  }, [cafeUuid]);

  async function redeem(amountPaidNumber) {
    const r = await fetch(
      `${API_BASE}/api/redeem/${encodeURIComponent(cafeUuid)}`,
      {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amountPaid: amountPaidNumber }),
      }
    );

    const data = await readJsonSafely(r);

    if (!r.ok || !data?.ok) {
      throw new Error(data?.error || `Redeem failed (${r.status})`);
    }

    setCtx((prev) => {
      if (!prev?.offer) return prev;
      return {
        ...prev,
        offer: {
          ...prev.offer,
          active: false,
          redeemed_at: data.redeemed_at,
          redeemed_cafe_id: data.redeemed_cafe_id,
          amount_paid: data.amount_paid,
        },
      };
    });

    return data;
  }

  if (loading) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Couldn't load redemption</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!ctx?.offer || !offerActive) {
    return (
      <div style={{ padding: 24 }}>
        <h2>{cafeName}</h2>
        <p>This offer has already been redeemed.</p>
        {ctx?.offer?.redeemed_at && (
          <div style={{ marginTop: 12, fontSize: 14, opacity: 0.8 }}>
            <div>Redeemed at: {ctx.offer.redeemed_at}</div>
            <div>Amount paid: ${Number(ctx.offer.amount_paid || 0).toFixed(2)}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <RedeemModal
        cafeLogo={cafeLogo}
        cafeSpace={cafeSpace}
        cafeLocation={cafeLocation}
        cafeName={cafeName}
        cafeCity={cafeCity}
        offerTitle={offerTitle}
        onRedeem={redeem}
      />
    </div>
  );
}
